// src/utils/dublinTime.js
//
// All times entered by users in the UI are treated as Dublin local time
// (Europe/Dublin). All times stored in MongoDB are UTC.
//
// These helpers convert explicitly in both directions, independent of
// whatever timezone the user's browser or OS happens to be set to.
// This means the app works correctly whether the browser is set to
// Dublin, Berlin, UTC, or anywhere else.
//
// DST is handled automatically — Intl.DateTimeFormat always knows
// Europe/Dublin's current offset for any given date.

const DUBLIN_TZ = 'Europe/Dublin';

// ─────────────────────────────────────────────────────────────────────────────
// CREATION HELPERS
// Use these when building UTC timestamps to send to the backend.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert a Dublin-local date string + time string into a UTC ISO string
 * suitable for sending to the backend / storing in MongoDB.
 *
 * @param {string} dateStr - 'YYYY-MM-DD' — the calendar date as the user sees it
 * @param {string} timeStr - 'HH:MM' — the wall-clock time in Dublin local time
 * @returns {string} UTC ISO 8601 string, e.g. '2030-07-10T07:00:00.000Z'
 *
 * @example
 * dublinLocalToUtcISO('2030-07-10', '08:00') // → '2030-07-10T07:00:00.000Z' (BST)
 * dublinLocalToUtcISO('2030-01-14', '08:00') // → '2030-01-14T08:00:00.000Z' (GMT)
 */
export function dublinLocalToUtcISO(dateStr, timeStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);

  // Step 1: Naive guess — treat the wall-clock time as if it were UTC.
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));

  // Step 2: Ask Intl what that UTC instant looks like when read in Dublin.
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: DUBLIN_TZ,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  const parts = dtf.formatToParts(utcGuess).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = parseInt(p.value, 10);
    return acc;
  }, {});

  // Step 3: Reconstruct what "the guess, read as Dublin time" looks like as UTC.
  const asIfDublin = Date.UTC(
    parts.year, parts.month - 1, parts.day,
    parts.hour, parts.minute, parts.second
  );

  // Step 4: The difference is Dublin's actual UTC offset for that date/time.
  const offsetMs = utcGuess.getTime() - asIfDublin;

  // Step 5: Apply the offset to get the correct UTC instant.
  return new Date(utcGuess.getTime() + offsetMs).toISOString();
}

/**
 * Build a full UTC ISO string for an overnight shift's end time.
 * The end date is automatically set to the next calendar day if
 * the end time is earlier than the start time (overnight shift).
 *
 * @param {string} startDateStr - 'YYYY-MM-DD' start date
 * @param {string} startTimeStr - 'HH:MM' start time (Dublin local)
 * @param {string} endTimeStr   - 'HH:MM' end time (Dublin local)
 * @returns {string} UTC ISO string for the end datetime
 */
export function dublinEndTimeToUtcISO(startDateStr, startTimeStr, endTimeStr) {
  const isOvernight = endTimeStr < startTimeStr; // e.g. '08:00' > '20:00' is false, '20:00' > '08:00' is true

  let endDateStr = startDateStr;
  if (isOvernight) {
    const [y, m, d] = startDateStr.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d));
    next.setUTCDate(next.getUTCDate() + 1);
    const yyyy = next.getUTCFullYear();
    const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(next.getUTCDate()).padStart(2, '0');
    endDateStr = `${yyyy}-${mm}-${dd}`;
  }

  return dublinLocalToUtcISO(endDateStr, endTimeStr);
}


// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY HELPERS
// Use these when showing stored UTC timestamps back to the user.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a UTC date value as a Dublin-local date string.
 * Accepts a Date object or any value accepted by `new Date()`.
 *
 * @param {Date|string|number} value
 * @param {Intl.DateTimeFormatOptions} [opts] - optional overrides
 * @returns {string} e.g. '14/01/2030'
 */
export function formatDublinDate(value, opts = {}) {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString('en-GB', { timeZone: DUBLIN_TZ, ...opts });
}

/**
 * Format a UTC date value as a Dublin-local time string (24h, HH:MM).
 *
 * @param {Date|string|number} value
 * @returns {string} e.g. '08:00'
 */
export function formatDublinTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleTimeString('en-GB', {
    timeZone: DUBLIN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format a UTC date value as a Dublin-local date+time string.
 *
 * @param {Date|string|number} value
 * @param {Intl.DateTimeFormatOptions} [opts]
 * @returns {string} e.g. '14/01/2030, 08:00'
 */
export function formatDublinDateTime(value, opts = {}) {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString('en-GB', {
    timeZone: DUBLIN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...opts,
  });
}

/**
 * Extract the Dublin-local date string ('YYYY-MM-DD') from a UTC value.
 * Use this when you need the calendar date as the user would see it in Dublin,
 * e.g. for date inputs, grouping by day, or comparing to stored date fields.
 *
 * @param {Date|string|number} value
 * @returns {string} 'YYYY-MM-DD'
 */
export function dublinDateStr(value) {
  const date = value instanceof Date ? value : new Date(value);
  // en-CA gives YYYY-MM-DD format directly
  return date.toLocaleDateString('en-CA', { timeZone: DUBLIN_TZ });
}

/**
 * Extract Dublin-local 'HH:MM' from a UTC value.
 * Use this when you need to populate a time input from a stored UTC timestamp.
 *
 * @param {Date|string|number} value
 * @returns {string} 'HH:MM'
 */
export function dublinTimeStr(value) {
  return formatDublinTime(value);
}

/**
 * Build the 'YYYY-MM-DDTHH:MM' string needed for a datetime-local input,
 * expressed in Dublin local time.
 *
 * @param {Date|string|number} value - UTC timestamp
 * @returns {string} e.g. '2030-07-10T08:00'
 */
export function dublinDateTimeInputStr(value) {
  const date = value instanceof Date ? value : new Date(value);
  const dateStr = dublinDateStr(date);   // YYYY-MM-DD
  const timeStr = dublinTimeStr(date);   // HH:MM
  return `${dateStr}T${timeStr}`;
}